'use client';
import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as _ from 'lodash';
import Cookies from 'js-cookie';
import {
  MagnifyingGlassIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowsUpDownIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DeleteIcon,
} from '@heroicons/react/24/outline';
import { RotatingLines } from 'react-loader-spinner';
import StrategyPieChart from './StrategyPieChart';
import mqtt from 'mqtt';

const TradesComponent = props => {
  const openTradesRef = useRef([]);
  const [openTrades, setOpenTrades] = useState([]);
  const [closedTrades, setClosedTrades] = useState([]);
  const [section, setSection] = useState('OpenTrades');
  const [client, setClient] = useState(null);
  const mqttClientRef = useRef(null);
  const wsRefBinance = useRef(null);
  const [lastUpdated, setLastUpdated] = useState(new Date().valueOf());
  const updateIntervalRef = useRef(null);

  // Subscribe on mount
  useEffect(() => {
    const handleBeforeUnload = () => {
      unsubscribeFromSymbols();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      unsubscribeFromSymbols();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    async function fetchData() {
      await getOpenTrades();
      await _connectMQTT(props);
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (!client) return;

    const handleMessage = (topic, message) => {
      if (topic === props.user.user_uuid + "_REFRESH_TRADES") {
        console.log("Refreshing Open and Closed Trades:", new Date().toLocaleTimeString());
        getOpenTrades();
        getClosedTrades();
      }
    };

    client.on('message', handleMessage);

    return () => {
      if (client) {
        client.off('message', handleMessage);
        if (mqttClientRef.current) {
          console.log('Disconnecting...', new Date().toLocaleTimeString());
          mqttClientRef.current.end();
          setClient(null);
        }
      }
    };
  }, [client, props.user.user_uuid]);

  // Sync ref with state
  useEffect(() => {
    openTradesRef.current = openTrades;
  }, [openTrades]);

  // UI update interval - trigger re-render every 3 seconds
  useEffect(() => {
    updateIntervalRef.current = setInterval(() => {
      // Force update by creating new array reference
      setOpenTrades(prevTrades => {
        // Check if ref has been updated
        if (openTradesRef.current !== prevTrades) {
          return [...openTradesRef.current];
        }
        return prevTrades;
      });
    }, 2000);

    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, []);

  // Connect to MQTT broker
  const _connectMQTT = async (props) => {
    if (!client) {
      let clientID = 'GoldenSparrowCapital-' + Math.random().toString(16).substring(2, 8);
      const gsccookie = Cookies.get('gsccookie') || null;
      const newClient = mqtt.connect(process.env.REACT_APP_EXTERNAL_MQTT_SERVER_URL, {
        clientId: clientID,
        username: 'user-jwt',
        password: gsccookie,
        clean: true,
        rejectUnauthorized: true,
        reconnectPeriod: 60000,
      });
      mqttClientRef.current = newClient;

      newClient.on('connect', async () => {
        console.log('Connected to MQTT:', new Date().toLocaleTimeString());

        newClient.subscribe(props.user.user_uuid + "_REFRESH_TRADES", err => {
          if (!err) {
            console.log('Topic subscribed:', props.user.user_uuid + "_REFRESH_TRADES");
          } else {
            console.error('Topic Subscribe error', err);
          }
        });
      });

      setClient(newClient);
    }
  };

  // Function to subscribe to real-time Binance prices
  const subscribeToSymbols = async (trades) => {
    // Don't unsubscribe if already connected - just let it run
    if (wsRefBinance.current && wsRefBinance.current.readyState === WebSocket.OPEN) {
      console.log("WebSocket already connected, skipping re-subscription");
      return;
    }

    unsubscribeFromSymbols();
    
    if (!trades || trades.length === 0) {
      console.log("No trades to subscribe to");
      return;
    }

    console.log("Subscribing to symbols for real-time prices...", trades.length);
    const streams = trades.map(w => `${w.symbol.toLowerCase()}@bookTicker`).join('/');
    const url = `wss://stream.binance.com:9443/stream?streams=${streams}`;

    wsRefBinance.current = new WebSocket(url);

    wsRefBinance.current.onopen = () => {
      console.log("Binance WebSocket connected");
    };

    wsRefBinance.current.onmessage = event => {
      const message = JSON.parse(event.data);
      const symbol = message.data.s; // Already uppercase from Binance
      const bidPrice = message.data.b; // Best bid (buy) price
      const askPrice = message.data.a; // Best ask (sell) price

      // Update the ref directly - this is more efficient
      openTradesRef.current = openTradesRef.current.map(item => {
        if (item.symbol.toUpperCase() === symbol.toUpperCase()) {
          const newPrice = item.tradeType === 'BUY'
            ? Number(askPrice).toFixed(item.numberOfDecimalValues)
            : Number(bidPrice).toFixed(item.numberOfDecimalValues);
          
          return {
            ...item,
            currentPrice: newPrice,
          };
        }
        return item;
      });
    };

    wsRefBinance.current.onerror = err => {
      console.error("Binance WebSocket error:", err);
    };

    wsRefBinance.current.onclose = () => {
      console.log("Binance WebSocket closed");
      // Attempt to reconnect after 5 seconds if there are still trades
      setTimeout(() => {
        if (openTradesRef.current.length > 0) {
          console.log("Attempting to reconnect WebSocket...");
          subscribeToSymbols(openTradesRef.current);
        }
      }, 5000);
    };
  };

  // Function to unsubscribe/cleanup
  const unsubscribeFromSymbols = () => {
    if (wsRefBinance.current) {
      wsRefBinance.current.close();
      wsRefBinance.current = null;
      console.log('Unsubscribed from Binance prices');
    }
  };

  const getOpenTrades = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/dashboard/getOpenTrades`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        setOpenTrades([]);
        return;
      }

      const data = await response.json();
      
      if (data.success && Array.isArray(data.data)) {
        setOpenTrades(data.data);
        openTradesRef.current = data.data;
        subscribeToSymbols(data.data);
      } else {
        console.error('Invalid response format:', data.message || 'Unknown error');
      }
    } catch (e) {
      console.error('Error fetching open trades:', e);
    }
  };

  const getClosedTrades = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/dashboard/getClosedTrades`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        setClosedTrades([]);
        return;
      }

      const data = await response.json();
      
      if (data.success && Array.isArray(data.data)) {
        setClosedTrades(data.data);
      } else {
        console.error('Invalid response format:', data.message || 'Unknown error');
      }
    } catch (e) {
      console.error('Error fetching closed trades:', e);
    }
  };

  // Helper: Aggregate profit/loss by strategy
  const aggregateStrategyPnL = (trades, isClosed) => {
    const stratMap = {};
    trades.forEach(trade => {
      const strat = trade.strategyName || 'No Strategy';
      let profitLoss = 0;
      if (isClosed) {
        profitLoss = (trade?.totalBuyQuantity * Number(trade.sellingPrice) - trade?.totalBuyQuantity * Number(trade.buyingPrice));
      } else {
        if (trade?.currentPrice) {
          profitLoss = trade?.totalBuyQuantity * (Number(trade?.currentPrice) - (trade.tradeType === "BUY" ? Number(trade.buyingPrice) : Number(trade.sellingPrice)));
        }
      }
      if (!stratMap[strat]) stratMap[strat] = { profit: 0, loss: 0 };
      if (profitLoss >= 0) {
        stratMap[strat].profit += profitLoss;
      } else {
        stratMap[strat].loss += profitLoss;
      }
    });
    return stratMap;
  };

  const openStrategyPnL = aggregateStrategyPnL(openTrades, false);
  const closedStrategyPnL = aggregateStrategyPnL(closedTrades, true);

  return (
    <div style={{ borderRadius: 8, width: 'auto' }}>
      <div style={{ marginTop: 5, display: 'flex', gap: 3 }}>
        <button
          style={{
            backgroundColor: section === "OpenTrades" ? '#fff' : "gray",
            color: section === "OpenTrades" ? "#282828" : "#fff",
            borderTopRightRadius: 8,
            borderTopLeftRadius: 8,
            padding: 6,
            fontSize: 14
          }}
          onClick={() => {
            setSection('OpenTrades');
            getOpenTrades();
          }}
        >
          Open Trades ({openTrades.length})
        </button>
        <button
          style={{
            backgroundColor: section === "ClosedTrades" ? '#fff' : "gray",
            color: section === "ClosedTrades" ? "#282828" : "#fff",
            borderTopRightRadius: 8,
            borderTopLeftRadius: 8,
            padding: 6,
            fontSize: 14
          }}
          onClick={() => {
            setSection('ClosedTrades');
            getClosedTrades();
          }}
        >
          Closed Trades
          {closedTrades.length > 0 && ` (${closedTrades.length})`}
        </button>
      </div>
      <hr></hr>

      {section === "OpenTrades" ? (
        <div>
          {/* Pie charts for each strategy */}
          {openTrades.length > 0 ? 
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, margin: '16px 0' }}>
            {Object.entries(openStrategyPnL).map(([strategy, { profit, loss }]) => (
              <StrategyPieChart
                key={strategy}
                strategyName={strategy}
                profit={profit}
                loss={loss}
              />
            ))}
          </div> : null }
          {openTrades.length > 0 ? (
            <div style={{ padding: 5 }}>
              <table className="min-w-full bg-white dark:bg-gray-800 shadow-md rounded-lg">
                <thead>
                  <tr>
                    <th className="text-sm py-2 px-4 border-b">S.No.</th>
                    <th className="text-sm py-2 px-4 border-b">Date & Time</th>
                    <th className="text-sm py-2 px-4 border-b">Symbol</th>
                    {props.user?.userType === 'ADMIN' && (
                      <th className="text-sm py-2 px-4 border-b">Strategy Name</th>
                    )}
                    <th className="text-sm py-2 px-4 border-b">Trade Type</th>
                    <th className="text-sm py-2 px-4 border-b">Trade Price</th>
                    <th className="text-sm py-2 px-4 border-b">Stop Loss Price</th>
                    <th className="text-sm py-2 px-4 border-b">Profit/Loss</th>
                  </tr>
                </thead>
                <tbody>
                  {_.orderBy(openTrades, 'tradeOpenDateTime', 'desc').map((trade, index) => {
                    const profitLoss = trade?.currentPrice
                      ? (
                          trade?.totalBuyQuantity *
                          (Number(trade?.currentPrice) - 
                           (trade.tradeType === "BUY" ? Number(trade.buyingPrice) : Number(trade.sellingPrice)))
                        ).toFixed(2)
                      : null;

                    return (
                      <tr key={trade.trade_uuid}>
                        <td className="text-xs py-2 px-4 border-b text-center">
                          {index + 1}.
                        </td>
                        <td className="text-xs py-2 px-4 border-b text-center">
                          {new Date(trade.tradeOpenDateTime).toLocaleDateString() + ', ' +
                           new Date(trade.tradeOpenDateTime).toLocaleTimeString(undefined, { hour12: true })}
                        </td>
                        <td className="py-2 px-4 border-b">
                          <button
                            style={{
                              backgroundColor: '#0474c9',
                              fontSize: 12,
                              borderRadius: 5,
                              padding: 5,
                            }}
                            onClick={() =>
                              window.open(
                                'https://www.tradingview.com/chart/?symbol=BINANCE%3A' + trade.symbol,
                                '_blank'
                              )
                            }
                          >
                            <div style={{ alignItems: 'center', display: 'flex' }}>
                              <h4 style={{ fontWeight: 'bolder', paddingLeft: 2 }}>{trade.symbol}</h4>
                              {trade?.currentPrice && (
                                <h6 style={{ paddingLeft: 5 }}>
                                  ${parseFloat(trade?.currentPrice).toFixed(trade.numberOfDecimalValues)}
                                </h6>
                              )}
                            </div>
                          </button>
                        </td>
                        {props.user?.userType === 'ADMIN' && (
                          <td className="text-xs py-2 px-4 border-b text-center">
                            {trade.strategyName}
                          </td>
                        )}
                        <td className="text-xs py-2 px-4 border-b text-center">
                          {trade.tradeType === "BUY" ? "Bought" : "Sold"} ({trade.totalBuyQuantity})
                        </td>
                        <td className="text-xs py-2 px-4 border-b text-center">
                          ${trade.tradeType === "BUY" 
                            ? Number(trade.buyingPrice).toFixed(trade.numberOfDecimalValues)
                            : Number(trade.sellingPrice).toFixed(trade.numberOfDecimalValues)}
                        </td>
                        <td className="text-xs py-2 px-4 border-b text-center" style={{ color: "grey" }}>
                          ${Number(trade?.stopLossPrice).toFixed(trade.numberOfDecimalValues)}
                        </td>
                        <td className="text-xs py-2 px-4 border-b text-center">
                          {profitLoss !== null ? (
                            <span style={{
                              borderRadius: 5,
                              padding: 5,
                              color: "#fff",
                              backgroundColor: parseFloat(profitLoss) >= 0 ? "green" : "red"
                            }}>
                              ${profitLoss}
                            </span>
                          ) : (
                            <span style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '100%',
                            }}>
                              <RotatingLines
                                visible={true}
                                height="22"
                                width="22"
                                color="grey"
                                strokeWidth="3"
                                animationDuration="1"
                                ariaLabel="rotating-lines-loading"
                              />
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: 5 }}>
              <h5 style={{ margin: 0, padding: 0, color: 'red', fontSize: 12 }}>No open trades yet.</h5>
            </div>
          )}
        </div>
      ) : null}

      {section === "ClosedTrades" ? (
        <div>
          {/* Pie charts for each strategy */}
           {closedTrades.length > 0 ?
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, margin: '16px 0' }}>
            {Object.entries(closedStrategyPnL).map(([strategy, { profit, loss }]) => (
              <StrategyPieChart
                key={strategy}
                strategyName={strategy}
                profit={profit}
                loss={loss}
              />
            ))}
          </div> : null }
          {closedTrades.length > 0 ? (
            <div style={{ padding: 5 }}>
              <table className="min-w-full bg-white dark:bg-gray-800 shadow-md rounded-lg">
                <thead>
                  <tr>
                    <th className="text-sm py-2 px-4 border-b">S.No.</th>
                    <th className="text-sm py-2 px-4 border-b">Open Date & Time</th>
                    <th className="text-sm py-2 px-4 border-b">Closed Date & Time</th>
                    <th className="text-sm py-2 px-4 border-b">Symbol</th>
                    {props.user?.userType === 'ADMIN' && (
                      <th className="text-sm py-2 px-4 border-b">Strategy Name</th>
                    )}
                    <th className="text-sm py-2 px-4 border-b">Trade Type</th>
                    <th className="text-sm py-2 px-4 border-b">Bought Price</th>
                    <th className="text-sm py-2 px-4 border-b">Sold Price</th>
                    <th className="text-sm py-2 px-4 border-b">Profit/Loss</th>
                  </tr>
                </thead>
                <tbody>
                  {_.orderBy(closedTrades, 'tradeCloseDateTime', 'desc').map((trade, index) => {
                    const profitLoss = (
                      trade?.totalBuyQuantity * Number(trade.sellingPrice) -
                      trade?.totalBuyQuantity * Number(trade.buyingPrice)
                    ).toFixed(2);

                    return (
                      <tr key={trade.trade_uuid}>
                        <td className="text-xs py-2 px-4 border-b text-center">
                          {index + 1}.
                        </td>
                        <td className="text-xs py-2 px-4 border-b text-center">
                          {new Date(trade.tradeOpenDateTime).toLocaleDateString() + ', ' +
                           new Date(trade.tradeOpenDateTime).toLocaleTimeString(undefined, { hour12: true })}
                        </td>
                        <td className="text-xs py-2 px-4 border-b text-center">
                          {new Date(trade.tradeCloseDateTime).toLocaleDateString() + ', ' +
                           new Date(trade.tradeCloseDateTime).toLocaleTimeString(undefined, { hour12: true })}
                        </td>
                        <td className="py-2 px-4 border-b">
                          <button
                            style={{
                              backgroundColor: 'grey',
                              fontSize: 12,
                              borderRadius: 5,
                              padding: 5,
                            }}
                            onClick={() =>
                              window.open(
                                'https://www.tradingview.com/chart/?symbol=BINANCE%3A' + trade.symbol,
                                '_blank'
                              )
                            }
                          >
                            <h4 style={{ fontWeight: 'bolder', paddingLeft: 2 }}>{trade.symbol}</h4>
                          </button>
                        </td>
                        {props.user?.userType === 'ADMIN' && (
                          <td className="text-xs py-2 px-4 border-b text-center">
                            {trade.strategyName}
                          </td>
                        )}
                        <td className="text-xs py-2 px-4 border-b text-center">
                          {trade.tradeType} ({trade.totalBuyQuantity})
                        </td>
                        <td className="text-xs py-2 px-4 border-b text-center">
                          ${Number(trade?.buyingPrice).toFixed(trade.numberOfDecimalValues)}
                        </td>
                        <td className="text-xs py-2 px-4 border-b text-center">
                          ${Number(trade?.sellingPrice).toFixed(trade.numberOfDecimalValues)}
                        </td>
                        <td className="text-xs py-2 px-4 border-b text-center">
                          <span style={{
                            borderRadius: 5,
                            padding: 5,
                            color: "#fff",
                            backgroundColor: parseFloat(profitLoss) > 0 ? 'green' : 'red'
                          }}>
                            ${profitLoss}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: 5 }}>
              <h5 style={{ margin: 0, padding: 0, color: 'red', fontSize: 12 }}>No closed trades yet.</h5>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default TradesComponent;